import React, { ReactNode, useCallback, useMemo } from 'react';
import cuid from 'cuid';
import WildcardTag from './WildcardTag';
import { Box } from '@televet/kibble-ui/build/chakra';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import Linkify from 'linkifyjs/react';
import * as linkify from 'linkifyjs';
import { WildcardInputTypes } from '.';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import { Dict } from 'pages/Reporting/QuickSightReports/embedDashboard';

export interface WildcardInputProps {
  containerStyles?: Dict;
  inputFooter?: ReactNode;
  inputLabel?: ReactNode;
  inputType: WildcardInputTypes;
  inputValue: string;
  isInputFocused: boolean;
  placeholder?: string;
  onInputValueChange: (value: string) => void;
  setIsInputFocused: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMenuFocused: React.Dispatch<React.SetStateAction<boolean>>;
  showPreview: boolean;
  textAreaMinH?: string;
  textAreaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  textInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  isInvalid?: boolean;
  errorText?: string;
  availableWildCards: WildcardValues[];
}

const WildcardInput = ({
  containerStyles,
  inputFooter,
  inputLabel,
  inputType,
  inputValue,
  isInputFocused,
  placeholder = '',
  isInvalid = false,
  onInputValueChange,
  setIsInputFocused,
  setIsMenuFocused,
  showPreview,
  textAreaMinH,
  textAreaRef,
  textInputRef,
  errorText,
  availableWildCards,
}: WildcardInputProps): JSX.Element => {
  const handleBlur = (): void => {
    setTimeout(() => {
      setIsMenuFocused(false);
      setIsInputFocused(false);
    }, 200);
  };

  // split on {{wildcard}}, new line, or new word
  const segments = useMemo(() => inputValue?.split(/({{.+?}})/g).filter(Boolean) || [], [inputValue]);

  const handleTextChange = useCallback(
    (newValue: string): void => {
      onInputValueChange(newValue);
    },
    [onInputValueChange],
  );

  const removeWildcard = useCallback(
    (wildCardValue: WildcardValues, startIndex: number): void => {
      const start = inputValue.slice(0, startIndex);
      const end = inputValue.slice(startIndex + wildCardValue.length);

      handleTextChange(start + end);
    },
    [handleTextChange, inputValue],
  );

  const getWildcardTag = useCallback(
    (text: string, startIndex: number): JSX.Element | string => {
      const wildcardValue = Object.values(availableWildCards).find((value) => value === text);
      return wildcardValue ? (
        <WildcardTag
          key={cuid()}
          value={wildcardValue}
          startIndex={startIndex}
          showPreview={!!showPreview}
          removeWildcard={removeWildcard}
        />
      ) : linkify.find(text).length && showPreview ? (
        <Linkify
          key={cuid()}
          tagName="span"
          options={{
            target: '_blank',
            attributes: { style: { color: 'text.interactive' } as any }, // eslint-disable-line @typescript-eslint/no-explicit-any
          }}
        >
          {text}
        </Linkify>
      ) : (
        <React.Fragment key={cuid()}>{text}</React.Fragment>
      );
    },
    [showPreview, removeWildcard, availableWildCards],
  );

  const handlePreviewClick = useCallback(() => {
    const inputRef = inputType === WildcardInputTypes.TextInput ? textInputRef?.current : textAreaRef?.current;
    if (inputRef) {
      setIsMenuFocused(false);
      setIsInputFocused(true);
      setTimeout(() => {
        inputRef.focus();
      }, 100);
    }
  }, [textInputRef, textAreaRef, setIsMenuFocused, setIsInputFocused, inputType]);

  const formattedContent = useMemo((): JSX.Element => {
    return segments.length ? (
      <Box>
        {segments.map((string, index) => {
          const startIndex = segments.slice(0, index).reduce((prev, next) => (prev += next.length), 0);

          return string === '\n' ? <Box key={`segment_${index}`} h={4} w="100%" /> : getWildcardTag(string, startIndex);
        })}
      </Box>
    ) : (
      <Box as="span" opacity={0.3}>
        {placeholder}
      </Box>
    );
  }, [getWildcardTag, placeholder, segments]);

  const invisibleStyles = {
    borderColor: 'transparent',
    boxShadowColor: 'transparent',
  };

  const borderColor = useMemo(() => {
    if (isInvalid) return 'border.danger';
    if (isInputFocused) return 'border.focus';

    return 'border.default';
  }, [isInvalid, isInputFocused]);

  const boxShadowColor = useMemo(() => {
    if (isInvalid) return 'var(--chakra-colors-border-danger)';
    if (isInputFocused) return 'var(--chakra-colors-border-focus)';

    return 'border.default';
  }, [isInvalid, isInputFocused]);

  return (
    <Box className="WildCardInputGroup__Container" mb={2} {...containerStyles}>
      {inputLabel}
      <Box
        className="WildCardInputGroup__Preview"
        whiteSpace="pre-wrap"
        onClick={handlePreviewClick}
        border="1px"
        borderColor={borderColor}
        boxShadow={`0 0 0 1px ${boxShadowColor}`}
        borderRadius="base"
        bg="background.default"
        fontSize="md"
        width="100%"
        _hover={{
          borderColor: isInvalid ? 'border.danger' : isInputFocused ? 'border.focus' : 'border.strong',
          cursor: 'text',
        }}
        overflow="hidden"
        position="relative"
      >
        <Box
          lineHeight="short"
          visibility={showPreview ? 'visible' : 'hidden'}
          position="absolute"
          top="9px"
          bottom="9px"
          left="17px"
          right="17px"
        >
          {formattedContent}
        </Box>
        {inputType === WildcardInputTypes.TextInput ? (
          <TextInput
            className="WildCardInputGroup__TextInput"
            ref={textInputRef}
            value={inputValue}
            placeholder={placeholder}
            onChange={(e): void => handleTextChange(e.target.value)}
            onBlur={handleBlur}
            onFocus={(): void => setIsInputFocused(true)}
            borderColor={'transparent'}
            _hover={invisibleStyles}
            _focus={invisibleStyles}
            visibility={!showPreview ? 'visible' : 'hidden'}
          />
        ) : (
          <Textarea
            className="WildCardInputGroup__TextArea"
            ref={textAreaRef}
            rows={3}
            value={inputValue}
            placeholder={placeholder}
            borderColor={'transparent'}
            onChange={(e): void => handleTextChange(e.target.value)}
            onFocus={(): void => setIsInputFocused(true)}
            onBlur={handleBlur}
            _hover={invisibleStyles}
            _focus={invisibleStyles}
            resize="vertical"
            minH={textAreaMinH !== undefined ? textAreaMinH : 10}
            visibility={!showPreview ? 'visible' : 'hidden'}
          />
        )}
      </Box>
      {isInvalid && (
        <Text mt={2} size="xs" as="p" variant="danger">
          {errorText ? errorText : 'This field contains an error'}
        </Text>
      )}
      {inputFooter}
    </Box>
  );
};

export default WildcardInput;
